import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PackageOpen, Search } from "lucide-react";

import { Input } from "../../../components/ui/input";
import { Skeleton } from "../../../components/feedback/Skeleton";
import { EmptyState } from "../../../components/feedback/EmptyState";
import { ErrorState } from "../../../components/feedback/ErrorState";
import { StatusBadge } from "../../../components/ui/StatusBadge";
import { Pagination } from "../../../components/ui/Pagination";
import { MultiSelectFilter } from "../../../components/forms/MultiSelectFilter";
import { collectionService } from "../../../services/deliveryCollection";
import { COLLECTION_QUEUE_STATUSES } from "../../orders/lifecycle";
import { TransitionModal } from "../../orders/components/TransitionModal";
import { useQueue } from "../../../hooks/useQueue";
import { formatDate } from "../../../utils/dates";

function eventEndedLabel(days, t) {
  if (days === null || days === undefined) return "—";
  if (days === 0) return t("admin.collection.event_ends_today");
  if (days > 0) return t("admin.collection.event_ended_ago", { count: days });
  return t("admin.collection.event_ends_in", { count: -days });
}

export default function CollectionPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queue = useQueue({
    fetcher: collectionService.queue,
    tError: t("common.error_generic"),
  });
  const [transitionOrder, setTransitionOrder] = useState(null);

  const statusOptions = useMemo(
    () =>
      COLLECTION_QUEUE_STATUSES.map((s) => ({
        value: s,
        label: t(`orders.status.${s}`),
      })),
    [t]
  );

  const totalPages = Math.max(1, Math.ceil(queue.total / queue.pageSize));
  const rangeStart = queue.total === 0 ? 0 : (queue.page - 1) * queue.pageSize + 1;
  const rangeEnd = Math.min(queue.page * queue.pageSize, queue.total);

  return (
    <>
      <div className="space-y-6" data-testid="collection-page">
        <header className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <PackageOpen className="h-5 w-5 text-champagne" aria-hidden />
            <h1
              className="text-3xl font-semibold tracking-tight text-text-primary"
              data-testid="collection-title"
            >
              {t("admin.collection.title")}
            </h1>
          </div>
          <p className="text-sm text-text-secondary">
            {t("admin.collection.subtitle")}
          </p>
        </header>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary"
              aria-hidden
            />
            <Input
              value={queue.search}
              onChange={(e) => queue.setSearch(e.target.value)}
              placeholder={t("admin.collection.search_placeholder")}
              data-testid="collection-search"
              className="h-10 pl-9 bg-surface border-border-subtle focus-visible:ring-champagne"
            />
          </div>
          <MultiSelectFilter
            selected={queue.statusFilter}
            options={statusOptions}
            onChange={queue.setStatusFilter}
            triggerLabel={t("admin.collection.filter_status")}
            testIdRoot="collection-status-filter"
          />
        </div>

        {queue.error ? (
          <ErrorState
            title={t("common.error_generic")}
            body={queue.error}
            onRetry={queue.reload}
            testId="collection-error"
          />
        ) : queue.loading ? (
          <div className="space-y-2" data-testid="collection-loading">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : queue.items.length === 0 ? (
          <EmptyState
            title={t("admin.collection.empty_title")}
            body={t("admin.collection.empty_body")}
            testId="collection-empty"
          />
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-border-subtle bg-surface shadow-card">
              <table className="min-w-full text-sm">
                <thead className="bg-offwhite text-left text-[11px] uppercase tracking-wide text-text-secondary">
                  <tr>
                    <th className="px-4 py-3 font-semibold">{t("admin.collection.col_order_number")}</th>
                    <th className="px-4 py-3 font-semibold">{t("admin.collection.col_event")}</th>
                    <th className="px-4 py-3 font-semibold">{t("admin.collection.col_customer")}</th>
                    <th className="px-4 py-3 font-semibold">{t("admin.collection.col_delivery_address")}</th>
                    <th className="px-4 py-3 font-semibold">{t("admin.collection.col_until_date")}</th>
                    <th className="px-4 py-3 font-semibold">{t("admin.collection.col_event_ended")}</th>
                    <th className="px-4 py-3 font-semibold">{t("admin.collection.col_status")}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {queue.items.map((o) => (
                    <tr
                      key={o.id}
                      data-testid={`collection-row-${o.id}`}
                      onClick={() => navigate(`/orders/${o.id}`)}
                      className="cursor-pointer transition-colors hover:bg-offwhite/50"
                    >
                      <td className="px-4 py-3 font-mono text-xs text-text-primary">
                        {o.order_number ? `#${o.order_number}` : ""}
                      </td>
                      <td className="px-4 py-3">
                        <div className="max-w-[16rem] truncate text-text-primary">{o.event_name}</div>
                      </td>
                      <td className="px-4 py-3 text-text-primary">
                        {o.customer?.company || "—"}
                      </td>
                      <td className="px-4 py-3 text-text-secondary">
                        <div className="max-w-[14rem] truncate">
                          {o.delivery_address_snippet?.street || "—"}
                        </div>
                        <div className="text-xs">
                          {o.delivery_address_snippet?.postal_code}{" "}
                          {o.delivery_address_snippet?.city}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-text-primary">
                        {formatDate(o.until_date, i18n.language)}
                      </td>
                      <td className="px-4 py-3 text-text-secondary text-xs">
                        {eventEndedLabel(o.event_ended_days_ago, t)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={o.status} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTransitionOrder(o);
                          }}
                          data-testid={`collection-transition-${o.id}`}
                          className="rounded-md border border-border-subtle bg-surface px-3 py-1 text-xs font-medium text-text-primary transition-colors hover:bg-offwhite focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne"
                        >
                          {t("admin.transitions.action_label")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={queue.page}
              totalPages={totalPages}
              pageSize={queue.pageSize}
              onPageChange={queue.setPage}
              onPageSizeChange={queue.setPageSize}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              total={queue.total}
              testId="collection-pagination"
            />
          </>
        )}
      </div>

      <TransitionModal
        open={Boolean(transitionOrder)}
        onOpenChange={(v) => (v ? null : setTransitionOrder(null))}
        order={transitionOrder}
        onDone={() => queue.reload()}
      />
    </>
  );
}
